'use strict'

import { toValue, type MaybeRefOrGetter, type Ref } from 'vue'

import { yieldToMain } from '../utils/scheduler'

import type { ComposedMetaData } from '@xcel-next/composer-schema'

// debug data accumulator — mirrors the module-level array from the original mixin
const adSlotsData: Record<string, unknown> = {}

// how many ad slots we check in one go before yielding back to the main thread,
// so a pending user input/paint doesn't get stuck behind this whole loop
const SLOT_CHECK_CHUNK_SIZE = 4

export function useAdaSlotLoader (
  metaData: MaybeRefOrGetter<ComposedMetaData | undefined>,
  registerSlots: Ref<string[]>,
  deviceType: Ref<string | null>,
  screenOrientation: Ref<string | null>,
  initMoltenBundle: () => void
) {
  async function initAdaSlotLoader () {
    await checkSlotsToLoad()
    initMoltenBundle()
    if (process.env.NODE_ENV !== 'production') {
      console.groupCollapsed('adSlotData')
      console.table(adSlotsData)
      console.groupEnd()
    }
  }

  async function checkSlotsToLoad () {
    const slotChecks: Array<() => void> = []
    for (let i = 1; i <= 3; i++) slotChecks.push(() => checkSlot(`mobile_${i}`, 0, 768))
    slotChecks.push(() => checkSlot('wallpaper_1', 768))
    slotChecks.push(() => checkSlot('superbanner_1', 990))
    for (let i = 1; i <= 2; i++) slotChecks.push(() => checkSlot(`rectangle_${i}`, 768))
    slotChecks.push(() => checkSlot('skyscraper_1', 1165))
    if (!toValue(metaData)?.disallowNativeTeasers) {
      for (let i = 1; i <= 3; i++) slotChecks.push(() => checkSlot(`teaser_${10 + i}`))
      for (let i = 1; i <= 6; i++) slotChecks.push(() => checkSlot(`teaser_${13 + i}`))
    }
    slotChecks.push(() => checkSlot('dmofooter_1', 990))
    slotChecks.push(() => checkSlot('mobile_10', 0, 768))

    for (let i = 0; i < slotChecks.length; i += SLOT_CHECK_CHUNK_SIZE) {
      slotChecks.slice(i, i + SLOT_CHECK_CHUNK_SIZE).forEach(check => check())
      if (i + SLOT_CHECK_CHUNK_SIZE < slotChecks.length) {
        await yieldToMain()
      }
    }
  }

  function checkSlot (adSlotName: string, minViewportWidth = 0, maxViewportWidth = 0) {
    const viewportWidth = deviceType.value === 'tablet' && screenOrientation.value === 'portrait'
      ? 1165 // we will scale the viewport width to 1165px for portrait tablets
      : window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

    let viewportCheckSucceeded = false
    let blockingReason: string | null = null

    if ((minViewportWidth > 0 && viewportWidth >= minViewportWidth) ||
        (maxViewportWidth > 0 && viewportWidth < maxViewportWidth) ||
        (minViewportWidth === 0 && maxViewportWidth === 0)) {
      viewportCheckSucceeded = true
      registerSlots.value.push(adSlotName)
    }

    blockingReason = !viewportCheckSucceeded
      ? 'inappropriateViewport'
      : document.querySelector(`#${adSlotName}`) === null
        ? 'nodeNotAvailable'
        : null

    adSlotsData[adSlotName] = { minViewportWidth, maxViewportWidth, viewportCheckSucceeded, blockingReason }
  }

  return { initAdaSlotLoader, checkSlotsToLoad, checkSlot }
}
